import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const BusinessSearchContext = createContext();

export const useBusinessSearch = () => {
  const context = useContext(BusinessSearchContext);
  if (!context) {
    throw new Error('useBusinessSearch must be used within a BusinessSearchProvider');
  }
  return context;
};

export const BusinessSearchProvider = ({ children }) => {
  const [minimalBusinesses, setMinimalBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessDetails, setBusinessDetails] = useState(new Map()); // Cache for detailed business data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastManualSelectionTime, setLastManualSelectionTime] = useState(0); // Track manual selections
  const lastSyncTimeRef = useRef(null); // Track last sync time to prevent frequent syncs
  const abortControllerRef = useRef(null); // Abort in-flight requests
  const loadInProgressRef = useRef(false); // Prevent duplicate concurrent loads
  const cacheRef = useRef({ data: null, timestamp: null }); // In-memory cache
  const CACHE_TTL = 60 * 60 * 1000; // 1 hour cache TTL
  const CACHE_KEY = 'am_minimal_businesses_cache';

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Load minimal business data when authenticated
  useEffect(() => {
    if (user) {
      console.log('🚀 PERFORMANCE: User changed, loading minimal business data');
      loadMinimalBusinesses(false, false); // Background load without loading state
    } else {
      console.log('🔒 SECURITY: No user, clearing business data');
      setMinimalBusinesses([]);
      setSelectedBusiness(null);
      setBusinessDetails(new Map());
    }
  }, [user]);

  // Cleanup: cancel in-flight requests on unmount or navigation
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        console.log('🧹 Cleanup: Aborted in-flight business request');
      }
    };
  }, []);

  // Auto-select business from URL when location changes
  useEffect(() => {
    if (minimalBusinesses.length > 0) {
      // Skip auto-select if manual selection just happened (within 500ms)
      const timeSinceManualSelection = Date.now() - lastManualSelectionTime;
      if (timeSinceManualSelection < 500) {
        console.log('⏭️ AUTO-SELECT: Skipping (manual selection in progress)');
        return;
      }

      // Extract locationId from URL patterns - matches any route with /:locationId
      // Covers: /ranking/:locationId, /optimization/:locationId, /posts/:locationId, /report/:locationId
      // /business/:locationId, /reviews/:locationId, /medias/:locationId, /insights/:locationId
      // /suggestions/:locationId, /services/:locationId, /activity/:locationId, /profile/:locationId
      const urlMatch = location.pathname.match(/\/(?:ranking|optimization|posts|report|business|reviews-manager|reviews|media-approval|medias|library|insights|suggestions|services|activity|profile)\/([^/]+)/);
      const locationIdFromUrl = urlMatch?.[1];

      if (locationIdFromUrl) {
        // Find the business that matches the URL locationId
        const matchingBusiness = minimalBusinesses.find(business =>
          business.locationId === locationIdFromUrl ||
          business.id === locationIdFromUrl ||
          business.googleLocationId === locationIdFromUrl
        );

        if (matchingBusiness && (!selectedBusiness || selectedBusiness.id !== matchingBusiness.id)) {
          console.log('🔄 AUTO-SELECT: Setting business from URL:', {
            urlLocationId: locationIdFromUrl,
            matchingBusiness: matchingBusiness.businessName,
            currentPath: location.pathname
          });
          setSelectedBusiness(matchingBusiness);
        } else if (!matchingBusiness) {
          console.warn('⚠️ AUTO-SELECT: No business found for URL locationId:', locationIdFromUrl);
        }
      }
    }
  }, [location.pathname, minimalBusinesses, selectedBusiness, lastManualSelectionTime]);

  // Check if cache is still valid
  const isCacheValid = () => {
    if (!cacheRef.current.data || !cacheRef.current.timestamp) {
      return false;
    }
    const age = Date.now() - cacheRef.current.timestamp;
    const isValid = age < CACHE_TTL;
    if (!isValid) {
      console.log(`⏰ Cache expired (${Math.round(age / 60000)} minutes old, TTL: ${Math.round(CACHE_TTL / 60000)}m)`);
    }
    return isValid;
  };

  // Load from localStorage
  const loadFromLocalStorage = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        if (age < CACHE_TTL) {
          console.log(`📦 Loaded businesses from localStorage (${Math.round(age / 1000)}s old)`);
          return data;
        }
      }
    } catch (err) {
      console.warn('⚠️ Failed to load from localStorage:', err);
    }
    return null;
  };

  // Save to localStorage
  const saveToLocalStorage = (data) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
      console.log('💾 Saved businesses to localStorage');
    } catch (err) {
      console.warn('⚠️ Failed to save to localStorage:', err);
    }
  };

  // Load minimal business list (names + IDs only)
  const loadMinimalBusinesses = async (forceRefresh = false, showLoading = true) => {
    if (!user) return;

    // 🚀 CHECK CACHE: Use cache immediately without loading state
    if (!forceRefresh) {
      if (isCacheValid()) {
        console.log('✨ Using in-memory cache, skipping fetch');
        setMinimalBusinesses(cacheRef.current.data);
        return;
      }

      const localStorageData = loadFromLocalStorage();
      if (localStorageData) {
        console.log('📦 Found valid cache in localStorage');
        cacheRef.current = { data: localStorageData, timestamp: Date.now() };
        setMinimalBusinesses(localStorageData);

        if (localStorageData.length > 0 && !selectedBusiness) {
          setSelectedBusiness(localStorageData[0]);
        }
        return;
      }
    }

    // 🚀 DEDUPLICATION: Skip if load already in progress
    if (loadInProgressRef.current) {
      console.log('⏭️ DEDUP: Business load already in progress, skipping duplicate request');
      return;
    }

    // Cancel any previous in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      console.log('🛑 Cancelled previous in-flight business request');
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    loadInProgressRef.current = true;

    // Only show loading state when explicitly requested (e.g., on refresh button click)
    if (showLoading) {
      setLoading(true);
    }
    setError(null);

    try {
      console.log(`🚀 PERFORMANCE: Loading minimal business data for user ${user._id}${forceRefresh ? ' (force refresh)' : ' (background)'}`);

      const response = await axios.get('/api/businesses/minimal', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        signal: abortControllerRef.current.signal
      });

      const businesses = response.data || [];
      console.log('✅ Successfully loaded minimal businesses:', businesses.length);

      // Update caches
      cacheRef.current = { data: businesses, timestamp: Date.now() };
      saveToLocalStorage(businesses);
      setMinimalBusinesses(businesses);

      // Auto-select first business if none selected
      if (businesses.length > 0 && !selectedBusiness) {
        setSelectedBusiness(businesses[0]);
        console.log('✅ Auto-selected first business:', businesses[0].businessName);
      }

    } catch (err) {
      // Skip error handling if request was aborted
      if (err.name === 'CanceledError') {
        console.log('⏭️ Business load was cancelled');
        return;
      }

      console.error('❌ Error loading minimal businesses:', err);

      let errorMessage = 'Failed to load businesses.';
      if (err.response?.status === 401) {
        errorMessage = 'Authentication expired. Please reconnect.';
      } else if (err.response?.status === 404) {
        errorMessage = 'No businesses found. Please connect your Google Business Profile.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      loadInProgressRef.current = false;
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Load detailed data for a specific business (lazy loading)
  const loadBusinessDetails = async (locationId, force = false) => {
    if (!locationId) return null;
    
    // Check cache first (unless forced)
    if (!force && businessDetails.has(locationId)) {
      console.log(`📋 Using cached details for business ${locationId}`);
      return businessDetails.get(locationId);
    }
    
    try {
      console.log(`🔄 Loading details for business ${locationId}`);
      
      const response = await axios.get(`/api/businesses/${locationId}/details`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      const details = response.data;
      console.log(`✅ Loaded details for business: ${details.businessName}`);
      
      // Cache the details
      setBusinessDetails(prev => new Map(prev.set(locationId, details)));
      
      return details;
    } catch (err) {
      console.error(`❌ Error loading details for business ${locationId}:`, err);
      throw err;
    }
  };

  // Get cached business details
  const getBusinessDetails = (locationId) => {
    return businessDetails.get(locationId) || null;
  };

  // Get current feature from URL
  const getCurrentFeature = () => {
    const path = location.pathname;
    if (path.includes('/optimization/')) return 'optimization';
    if (path.includes('/business/')) return 'business';
    if (path.includes('/posts/')) return 'posts';
    if (path.includes('/reviews-manager/')) return 'reviews-manager';
    if (path.includes('/reviews/')) return 'reviews';
    if (path.includes('/ranking/')) return 'ranking';
    if (path.includes('/report/')) return 'report';
    if (path.includes('/media-approval/')) return 'media-approval';
    if (path.includes('/medias/')) return 'medias';
    if (path.includes('/library/')) return 'library';
    if (path.includes('/insights/')) return 'insights';
    if (path.includes('/suggestions/')) return 'suggestions';
    if (path.includes('/services/')) return 'services';
    if (path.includes('/activity/')) return 'activity';
    if (path.includes('/profile/')) return 'profile';
    return 'dashboard';
  };

  // Select a business (and optionally load its details)
  const selectBusiness = async (business, loadDetails = false) => {
    if (!business) {
      setSelectedBusiness(null);
      return null;
    }

    console.log('🔄 Selecting business:', business.businessName);
    setSelectedBusiness(business);
    setLastManualSelectionTime(Date.now()); // Mark time of manual selection

    // 🚀 Sync business profiles with 1-hour rate limit (non-blocking)
    const now = Date.now();
    const lastSyncTime = lastSyncTimeRef.current;
    const ONE_HOUR = 60 * 60 * 1000;

    if (lastSyncTime && now - lastSyncTime < ONE_HOUR) {
      const minutesUntilSync = Math.ceil((ONE_HOUR - (now - lastSyncTime)) / 60000);
      console.log(`⏭️ SYNC RATE LIMITED: Last sync was ${Math.round((now - lastSyncTime) / 60000)} minutes ago. Next sync available in ${minutesUntilSync} minutes.`);
    } else {
      // Execute sync in background without awaiting to not block business selection
      lastSyncTimeRef.current = now;
      axios.post('/api/sync/business-profiles', { force: false })
        .then(() => {
          console.log('✅ Business profiles synced successfully in background');
        })
        .catch((syncError) => {
          console.warn('⚠️ Failed to sync business profiles:', syncError.message);
        });
    }

    // 🔄 Get feature and location for navigation
    const currentFeature = getCurrentFeature();
    const newLocationId = business.locationId || business.googleLocationId || business.id;

    // 🚀 Smooth transition: delay navigation slightly to let React state update
    if (currentFeature !== 'dashboard' && newLocationId) {
      const newPath = `/${currentFeature}/${newLocationId}`;

      // Check if we're already on the correct path
      if (location.pathname !== newPath) {
        console.log('📍 Navigating to new business context:', {
          currentPath: location.pathname,
          newPath,
          businessName: business.businessName,
          locationId: newLocationId
        });

        // Small delay (0ms) ensures state updates propagate before navigation
        // This prevents race conditions with the auto-select effect
        setTimeout(() => {
          navigate(newPath);
        }, 0);
      } else {
        console.log('✅ Already on correct path, skipping navigation');
      }
    }

    if (loadDetails) {
      try {
        const details = await loadBusinessDetails(business.locationId || business.googleLocationId || business.id);
        return details;
      } catch (err) {
        console.error('Error loading business details on selection:', err);
        return business;
      }
    }

    return business;
  };

  // Search businesses by name or location
  const searchBusinesses = (searchTerm) => {
    if (!searchTerm) return minimalBusinesses;
    
    const term = searchTerm.toLowerCase();
    return minimalBusinesses.filter(business => {
      const businessName = (business.businessName || '').toLowerCase();
      const city = (business.address?.city || '').toLowerCase();
      return businessName.includes(term) || city.includes(term);
    });
  };

  // Get business by ID
  const getBusinessById = (businessId) => {
    return minimalBusinesses.find(b => 
      b.id === businessId || 
      b.locationId === businessId ||
      b.googleLocationId === businessId
    ) || null;
  };

  // Refresh business data
  const refreshBusinesses = async () => {
    console.log('🔄 Refreshing business data (force refresh)');
    setBusinessDetails(new Map()); // Clear detailed business cache
    cacheRef.current = { data: null, timestamp: null }; // Clear in-memory cache
    try {
      localStorage.removeItem(CACHE_KEY); // Clear localStorage cache
    } catch (err) {
      console.warn('⚠️ Failed to clear localStorage:', err);
    }
    await loadMinimalBusinesses(true, true); // Force fetch with loading state
  };

  // Clear cache for a specific business
  const clearBusinessCache = (locationId) => {
    setBusinessDetails(prev => {
      const newMap = new Map(prev);
      newMap.delete(locationId);
      return newMap;
    });
  };

  const value = {
    // Data
    minimalBusinesses,
    selectedBusiness,
    businessDetails: businessDetails,
    loading,
    error,
    
    // Methods
    loadMinimalBusinesses,
    loadBusinessDetails,
    getBusinessDetails,
    selectBusiness,
    searchBusinesses,
    getBusinessById,
    refreshBusinesses,
    clearBusinessCache,
    
    // State setters (for direct manipulation if needed)
    setSelectedBusiness,
    setError
  };

  return (
    <BusinessSearchContext.Provider value={value}>
      {children}
    </BusinessSearchContext.Provider>
  );
};